import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, User as UserIcon, MessageSquare, Eye, Mail, Phone, MapPin, DollarSign, Calendar, FileText, Bot, Trash2, Edit3, Save, X, Clock } from "lucide-react";
import { Link } from "react-router-dom";

// Format a lead's created_at into two strings: an absolute date and
// a compact relative time (e.g. "hace 2 h", "hace 3 d", "hoy 09:20").
// Returns null if the date is unparseable/missing so the UI can skip it.
function formatLeadDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  const dateShort = d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
  const dateLong = d.toLocaleDateString("es-ES", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  let relative;
  if (diffMs < 0) relative = "en el futuro";
  else if (diffMin < 1) relative = "ahora mismo";
  else if (diffMin < 60) relative = `hace ${diffMin} min`;
  else if (sameDay) relative = `hoy ${time}`;
  else if (diffH < 48) relative = `ayer ${time}`;
  else if (diffD < 7) relative = `hace ${diffD} d`;
  else relative = dateShort;
  return { relative, dateShort, dateLong, time, iso: d.toISOString() };
}

const STATUSES = ["nuevo", "calificando", "calificado", "agendado", "cotizado", "ganado", "perdido"];
const SCORE_STYLES = {
  ALTO: "border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5",
  MEDIO: "border-[#D95D39] text-[#D95D39] bg-[#D95D39]/5",
  BAJO: "border-[#8A8F98] text-[#8A8F98]",
  PENDIENTE: "border-[#E2DCD0] text-[#8A8F98]",
};

const SOURCE_LABELS = {
  "chat-web": { label: "Chat web", icon: Bot, color: "border-[#1877F2] text-[#1877F2] bg-[#1877F2]/5" },
  "mail-directo": { label: "Mail directo", icon: Mail, color: "border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5" },
  "campana-mailing": { label: "Campaña mailing", icon: FileText, color: "border-[#D95D39] text-[#D95D39] bg-[#D95D39]/5" },
  "referal": { label: "Referal", icon: UserIcon, color: "border-[#8B4789] text-[#8B4789] bg-[#8B4789]/5" },
  // Legacy compatibility
  "chat-publico": { label: "Chat web", icon: Bot, color: "border-[#1877F2] text-[#1877F2] bg-[#1877F2]/5" },
  "email-directo": { label: "Mail directo", icon: Mail, color: "border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5" },
  "formulario-web": { label: "Formulario web", icon: FileText, color: "border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5" },
  "meta-ads": { label: "Meta Ads", icon: FileText, color: "border-[#1877F2] text-[#1877F2] bg-[#1877F2]/5" },
  "google-ads": { label: "Google Ads", icon: FileText, color: "border-[#D95D39] text-[#D95D39] bg-[#D95D39]/5" },
  "manual": { label: "Manual", icon: UserIcon, color: "border-[#8A8F98] text-[#8A8F98]" },
};

function sourceMeta(source, customSources = []) {
  if (SOURCE_LABELS[source]) return SOURCE_LABELS[source];
  const custom = customSources.find((s) => s.key === source);
  if (custom) return { label: custom.label, icon: UserIcon, color: "border-[#5C616A] text-[#5C616A] bg-[#5C616A]/5" };
  return { label: source || "Manual", icon: UserIcon, color: "border-[#8A8F98] text-[#8A8F98]" };
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", destination_interest: "", budget: "", travel_dates: "", travelers: "", brief: "", notes: "", source: "mail-directo", referrer_name: "" });
  const [selectedLead, setSelectedLead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [mcSchema, setMcSchema] = useState([]);
  const [sources, setSources] = useState([]);
  const [showAddSource, setShowAddSource] = useState(false);
  const [newSourceLabel, setNewSourceLabel] = useState("");

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  const loadSources = () => api.get("/leads/sources/list").then((r) => setSources(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    loadSources();
    api.get("/mailchimp/active-merge-fields").then((r) => setMcSchema(r.data || [])).catch(() => {});
  }, []);

  const openLead = async (id) => {
    try {
      const r = await api.get(`/leads/${id}`);
      setSelectedLead(r.data);
      setEditing(false);
      setEditForm(null);
    } catch {
      const l = leads.find((x) => x.id === id);
      if (l) setSelectedLead(l);
    }
  };

  const beginEdit = () => {
    if (!selectedLead) return;
    setEditForm({
      name: selectedLead.name || "",
      email: selectedLead.email || "",
      phone: selectedLead.phone || "",
      destination_interest: selectedLead.destination_interest || "",
      budget: selectedLead.budget || "",
      travel_dates: selectedLead.travel_dates || "",
      travelers: selectedLead.travelers ?? "",
      brief: selectedLead.brief || "",
      notes: selectedLead.notes || "",
      status: selectedLead.status || "nuevo",
      source: selectedLead.source || "manual",
      referrer_name: selectedLead.referrer_name || "",
      mailchimp_data: { ...(selectedLead.mailchimp_data || {}) },
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!selectedLead || !editForm) return;
    const payload = { ...editForm };
    if (payload.travelers === "" || payload.travelers == null) delete payload.travelers;
    else payload.travelers = Number(payload.travelers) || undefined;
    if (payload.source !== "referal") payload.referrer_name = null;
    try {
      const r = await api.patch(`/leads/${selectedLead.id}`, payload);
      setSelectedLead(r.data);
      setEditing(false);
      setEditForm(null);
      toast.success("Lead actualizado. Sincronizando con Mailchimp...");
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  const create = async () => {
    if (!form.name || !form.email) return toast.error("Nombre y email son obligatorios");
    if (form.source === "referal" && !form.referrer_name?.trim()) {
      return toast.error("Indica quién recomendó al lead");
    }
    const payload = { ...form };
    if (payload.travelers === "" || payload.travelers == null) {
      delete payload.travelers;
    } else {
      payload.travelers = Number(payload.travelers) || undefined;
    }
    if (payload.source !== "referal") delete payload.referrer_name;
    await api.post("/leads", payload);
    toast.success("Lead creado");
    setForm({ name: "", email: "", phone: "", destination_interest: "", budget: "", travel_dates: "", travelers: "", brief: "", notes: "", source: "mail-directo", referrer_name: "" });
    setCreating(false);
    load();
  };

  const addNewSource = async () => {
    const label = newSourceLabel.trim();
    if (!label) return;
    try {
      const r = await api.post("/leads/sources/list", { label });
      await loadSources();
      setForm((f) => ({ ...f, source: r.data.key }));
      setNewSourceLabel("");
      setShowAddSource(false);
      toast.success(`Fuente "${label}" añadida`);
    } catch {
      toast.error("No se pudo añadir la fuente");
    }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    load();
  };

  const removeLead = async (id, name) => {
    if (!window.confirm(`¿Borrar el lead "${name || id}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead borrado");
      if (selectedLead?.id === id) setSelectedLead(null);
      load();
    } catch {
      toast.error("No se pudo borrar el lead");
    }
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  const grouped = STATUSES.reduce((acc, s) => {
    acc[s] = leads.filter((l) => l.status === s);
    return acc;
  }, {});

  return (
    <div className="stagger">
      <PageHeader
        overline="CRM"
        title="Pipeline de leads."
        subtitle="Sigue cada cliente potencial desde su captación hasta la conversión final."
        action={
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogTrigger asChild>
              <Button data-testid="lead-create-button" className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Nuevo lead
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Nuevo lead manual</DialogTitle>
                <p className="text-xs text-[#8A8F98] mt-1">Captura una solicitud que llegó por email, llamada o referencia.</p>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nombre *</Label><Input data-testid="lead-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label>Email *</Label><Input data-testid="lead-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label>Destino</Label><Input placeholder="Spain, Italy, Portugal..." value={form.destination_interest} onChange={(e) => setForm({ ...form, destination_interest: e.target.value })} className="mt-1 rounded-sm" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Viajeros</Label><Input type="number" min="1" value={form.travelers} onChange={(e) => setForm({ ...form, travelers: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label>Fechas</Label><Input placeholder="Sep 2026" value={form.travel_dates} onChange={(e) => setForm({ ...form, travel_dates: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label>Presupuesto</Label><Input placeholder="€5000/pp" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-1 rounded-sm" /></div>
                </div>

                {/* Source selector */}
                <div className="pt-2 border-t border-[#F0EBE2]">
                  <Label>Fuente del lead *</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {sources.map((s) => {
                      const active = form.source === s.key;
                      return (
                        <button
                          type="button"
                          key={s.key}
                          onClick={() => setForm({ ...form, source: s.key })}
                          data-testid={`source-chip-${s.key}`}
                          className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                            active
                              ? "bg-[#0A1128] text-white border-[#0A1128]"
                              : "bg-white text-[#5C616A] border-[#E2DCD0] hover:border-[#D95D39] hover:text-[#D95D39]"
                          }`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setShowAddSource((v) => !v)}
                      data-testid="source-add-button"
                      className="text-xs px-3 py-1.5 rounded-sm border border-dashed border-[#8A8F98] text-[#8A8F98] hover:border-[#D95D39] hover:text-[#D95D39]"
                    >
                      + Añadir nueva
                    </button>
                  </div>
                  {showAddSource && (
                    <div className="mt-2 flex gap-2">
                      <Input
                        placeholder="Nombre de la nueva fuente (p.ej. Evento Madrid)"
                        value={newSourceLabel}
                        onChange={(e) => setNewSourceLabel(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNewSource())}
                        className="rounded-sm text-sm"
                        data-testid="source-new-input"
                      />
                      <Button
                        type="button"
                        onClick={addNewSource}
                        className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm text-xs"
                        data-testid="source-new-save"
                      >
                        Guardar fuente
                      </Button>
                    </div>
                  )}
                  {form.source === "referal" && (
                    <div className="mt-3">
                      <Label className="text-xs">Persona que lo recomienda *</Label>
                      <Input
                        placeholder="Nombre y apellido (o empresa)"
                        value={form.referrer_name}
                        onChange={(e) => setForm({ ...form, referrer_name: e.target.value })}
                        className="mt-1 rounded-sm"
                        data-testid="referrer-input"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label>Brief / solicitud original</Label>
                  <Textarea
                    placeholder="Pega aquí el email original del cliente o resume su solicitud..."
                    value={form.brief}
                    onChange={(e) => setForm({ ...form, brief: e.target.value })}
                    className="mt-1 rounded-sm min-h-[140px]"
                  />
                </div>
                <div>
                  <Label>Notas internas</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="lead-form-submit" onClick={create} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">Crear lead</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Kanban */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {STATUSES.map((s) => (
            <div key={s} className="kanban-col p-3 w-72 shrink-0">
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">{s}</span>
                <span className="font-mono text-xs text-[#0A1128]">{grouped[s].length}</span>
              </div>
              <div className="space-y-2">
                {grouped[s].length === 0 && (
                  <div className="text-xs text-[#8A8F98] italic px-1 py-4">Sin leads</div>
                )}
                {grouped[s].map((l) => {
                  const src = sourceMeta(l.source, sources);
                  const SrcIcon = src.icon;
                  const t = formatLeadDate(l.created_at);
                  return (
                    <Card
                      key={l.id}
                      onClick={() => openLead(l.id)}
                      className="edit-card p-3 rounded-sm shadow-none cursor-pointer hover:border-[#D95D39] transition-colors"
                      data-testid={`lead-card-${l.id}`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <div className="font-medium text-sm text-[#0A1128] truncate">{l.name}</div>
                        <Badge variant="outline" className={`rounded-sm text-[9px] ${SCORE_STYLES[l.score]}`}>{l.score}</Badge>
                      </div>
                      <div className="text-xs text-[#5C616A] truncate">{l.email}</div>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <Badge variant="outline" className={`rounded-sm text-[9px] inline-flex items-center gap-1 ${src.color}`}>
                          <SrcIcon className="w-2.5 h-2.5" />{src.label}
                        </Badge>
                        {t && (
                          <span
                            className="text-[9px] text-[#8A8F98] inline-flex items-center gap-1"
                            title={t.dateLong + " · " + t.time}
                            data-testid={`lead-created-${l.id}`}
                          >
                            <Clock className="w-2.5 h-2.5" />{t.relative}
                          </span>
                        )}
                      </div>
                      {l.source === "referal" && l.referrer_name && (
                        <div className="text-[10px] text-[#8B4789] mt-1 italic">por {l.referrer_name}</div>
                      )}
                      {l.destination_interest && (
                        <div className="text-xs text-[#8A8F98] mt-1 italic">{l.destination_interest}</div>
                      )}
                      {l.summary && (
                        <div className="text-[11px] text-[#5C616A] mt-2 line-clamp-2">{l.summary}</div>
                      )}
                      <div className="mt-3 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                          <SelectTrigger className="h-7 text-[11px] rounded-sm" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button onClick={() => openLead(l.id)} size="icon" variant="outline" className="h-7 w-7 rounded-sm border-[#E2DCD0] shrink-0" data-testid={`lead-view-${l.id}`} title="Ver detalle">
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button
                          onClick={async () => { await openLead(l.id); beginEdit(); }}
                          size="icon"
                          variant="outline"
                          className="h-7 w-7 rounded-sm border-[#E2DCD0] text-[#0A1128] hover:text-[#D95D39] hover:border-[#D95D39] shrink-0"
                          data-testid={`lead-edit-${l.id}`}
                          title="Editar"
                        >
                          <Edit3 className="w-3 h-3" />
                        </Button>
                        <Button
                          onClick={() => removeLead(l.id, l.name)}
                          size="icon"
                          variant="outline"
                          className="h-7 w-7 rounded-sm border-[#E2DCD0] text-[#8A8F98] hover:text-[#c54e2e] hover:border-[#c54e2e] hover:bg-[#c54e2e]/5 shrink-0"
                          data-testid={`lead-delete-${l.id}`}
                          title="Borrar lead"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lead Detail Drawer */}
      <Dialog open={!!selectedLead} onOpenChange={(o) => !o && setSelectedLead(null)}>
        <DialogContent className="bg-white rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail-modal">
          {selectedLead && (() => {
            const src = sourceMeta(selectedLead.source, sources);
            const SrcIcon = src.icon;
            return (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <DialogTitle className="font-display text-2xl text-[#0A1128]">{selectedLead.name}</DialogTitle>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge variant="outline" className={`rounded-sm text-[10px] ${SCORE_STYLES[selectedLead.score]}`}>{selectedLead.score}</Badge>
                        <Badge variant="outline" className={`rounded-sm text-[10px] inline-flex items-center gap-1 ${src.color}`}>
                          <SrcIcon className="w-3 h-3" />{src.label}
                        </Badge>
                        <Badge variant="outline" className="rounded-sm text-[10px] border-[#E2DCD0] text-[#5C616A]">{selectedLead.status}</Badge>
                        {(() => {
                          const t = formatLeadDate(selectedLead.created_at);
                          return t ? (
                            <Badge
                              variant="outline"
                              className="rounded-sm text-[10px] border-[#E2DCD0] text-[#5C616A] inline-flex items-center gap-1"
                              title={t.dateLong + " · " + t.time}
                              data-testid="lead-detail-created-at"
                            >
                              <Clock className="w-3 h-3" />
                              Recibido {t.dateShort} · {t.time}
                            </Badge>
                          ) : null;
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!editing ? (
                        <>
                          <Button
                            onClick={beginEdit}
                            size="sm"
                            variant="outline"
                            className="rounded-sm border-[#E2DCD0] text-[#0A1128] hover:border-[#D95D39] hover:text-[#D95D39]"
                            data-testid="lead-detail-edit"
                          >
                            <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Editar
                          </Button>
                          <Button
                            onClick={() => removeLead(selectedLead.id, selectedLead.name)}
                            size="sm"
                            variant="outline"
                            className="rounded-sm border-[#E2DCD0] text-[#8A8F98] hover:text-[#c54e2e] hover:border-[#c54e2e] hover:bg-[#c54e2e]/5"
                            data-testid="lead-detail-delete"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Borrar
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() => { setEditing(false); setEditForm(null); }}
                            size="sm"
                            variant="outline"
                            className="rounded-sm border-[#E2DCD0] text-[#8A8F98]"
                          >
                            <X className="w-3.5 h-3.5 mr-1.5" /> Cancelar
                          </Button>
                          <Button
                            onClick={saveEdit}
                            size="sm"
                            className="rounded-sm bg-[#D95D39] hover:bg-[#c54e2e] text-white"
                            data-testid="lead-detail-save"
                          >
                            <Save className="w-3.5 h-3.5 mr-1.5" /> Guardar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </DialogHeader>

                {editing && editForm ? (
                  /* ---------- EDIT MODE ---------- */
                  <div className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Nombre</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 rounded-sm" /></div>
                      <div><Label className="text-xs">Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1 rounded-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Teléfono</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1 rounded-sm" /></div>
                      <div>
                        <Label className="text-xs">Status</Label>
                        <select
                          value={editForm.status}
                          onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                          className="mt-1 w-full bg-white border border-[#E2DCD0] rounded-sm px-3 py-2 text-sm"
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-2 pt-2 border-t border-[#F0EBE2]">Brief y datos del viaje</p>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div><Label className="text-xs">Destino</Label><Input value={editForm.destination_interest} onChange={(e) => setEditForm({ ...editForm, destination_interest: e.target.value })} className="mt-1 rounded-sm" /></div>
                        <div><Label className="text-xs">Fechas</Label><Input value={editForm.travel_dates} onChange={(e) => setEditForm({ ...editForm, travel_dates: e.target.value })} className="mt-1 rounded-sm" /></div>
                        <div><Label className="text-xs">Viajeros</Label><Input type="number" value={editForm.travelers} onChange={(e) => setEditForm({ ...editForm, travelers: e.target.value })} className="mt-1 rounded-sm" /></div>
                        <div><Label className="text-xs">Presupuesto</Label><Input value={editForm.budget} onChange={(e) => setEditForm({ ...editForm, budget: e.target.value })} className="mt-1 rounded-sm" /></div>
                      </div>
                      <Label className="text-xs">Brief / solicitud</Label>
                      <Textarea value={editForm.brief} onChange={(e) => setEditForm({ ...editForm, brief: e.target.value })} className="mt-1 rounded-sm min-h-[100px]" />
                    </div>
                    <div>
                      <Label className="text-xs">Notas internas</Label>
                      <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1 rounded-sm" />
                    </div>

                    {/* Source editor */}
                    <div className="pt-3 border-t border-[#F0EBE2]">
                      <Label className="text-xs">Fuente del lead</Label>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {sources.map((s) => {
                          const active = editForm.source === s.key;
                          return (
                            <button
                              type="button"
                              key={s.key}
                              onClick={() => setEditForm({ ...editForm, source: s.key })}
                              data-testid={`edit-source-chip-${s.key}`}
                              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                                active
                                  ? "bg-[#0A1128] text-white border-[#0A1128]"
                                  : "bg-white text-[#5C616A] border-[#E2DCD0] hover:border-[#D95D39] hover:text-[#D95D39]"
                              }`}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                      {editForm.source === "referal" && (
                        <div className="mt-3">
                          <Label className="text-xs">Persona que lo recomienda</Label>
                          <Input
                            placeholder="Nombre y apellido (o empresa)"
                            value={editForm.referrer_name || ""}
                            onChange={(e) => setEditForm({ ...editForm, referrer_name: e.target.value })}
                            className="mt-1 rounded-sm"
                            data-testid="edit-referrer-input"
                          />
                        </div>
                      )}
                    </div>

                    {mcSchema.length > 0 && (
                      <div className="pt-3 border-t border-[#F0EBE2]">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-2 inline-flex items-center gap-1.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFE01B]" />
                          Campos Mailchimp <span className="text-[#FFE01B]">·</span> {mcSchema.length}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {mcSchema.filter((f) => f.tag !== "FNAME" && f.tag !== "LNAME").map((f) => {
                            const v = (editForm.mailchimp_data || {})[f.tag];
                            const setVal = (val) => setEditForm({
                              ...editForm,
                              mailchimp_data: { ...(editForm.mailchimp_data || {}), [f.tag]: val },
                            });
                            const type = f.type === "number" ? "number" : (f.type === "date" ? "date" : "text");
                            const placeholder = f.type === "birthday" ? "MM/DD (ej. 06/20)" : "";
                            return (
                              <div key={f.tag}>
                                <Label className="text-xs">{f.name} <span className="text-[#8A8F98]">({f.tag} · {f.type})</span></Label>
                                <Input
                                  type={type}
                                  placeholder={placeholder}
                                  value={typeof v === "object" ? "" : (v ?? "")}
                                  onChange={(e) => setVal(e.target.value)}
                                  className="mt-1 rounded-sm text-sm"
                                  data-testid={`mc-field-${f.tag}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-[#8A8F98] mt-3">Al guardar, los cambios se sincronizan automáticamente con Mailchimp.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ---------- VIEW MODE ---------- */
                  <></>
                )}


                {editing && editForm ? null : (
                <>
                {/* 1. CONTACT INFO (first) */}
                <div className="grid sm:grid-cols-2 gap-3 mt-2 pb-4 border-b border-[#E2DCD0]">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-[#8A8F98]" />
                    <a href={`mailto:${selectedLead.email}`} className="text-[#D95D39] hover:underline truncate">{selectedLead.email}</a>
                  </div>
                  {selectedLead.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-[#8A8F98]" />
                      <a href={`tel:${selectedLead.phone}`} className="text-[#D95D39] hover:underline">{selectedLead.phone}</a>
                    </div>
                  )}
                  {selectedLead.mailchimp_id && (
                    <div className="flex items-center gap-2 text-xs text-[#8A8F98] col-span-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#FFE01B]/15 border border-[#FFE01B]/40 text-[#1A1F2B]">
                        Mailchimp · {selectedLead.mailchimp_status || "synced"}
                      </span>
                    </div>
                  )}
                  {selectedLead.source === "referal" && selectedLead.referrer_name && (
                    <div className="flex items-center gap-2 text-sm col-span-2">
                      <UserIcon className="w-4 h-4 text-[#8B4789]" />
                      <span className="text-[#1A1F2B]">
                        <span className="text-[10px] uppercase tracking-wider text-[#8A8F98] mr-1.5">Recomendado por</span>
                        <span className="font-medium">{selectedLead.referrer_name}</span>
                      </span>
                    </div>
                  )}
                </div>

                {/* 2. BRIEF (qué pidió + datos estructurados) */}
                {(selectedLead.brief || selectedLead.destination_interest || selectedLead.budget || selectedLead.travel_dates || selectedLead.travelers) && (
                  <div className="py-4 border-b border-[#E2DCD0]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-2.5">Qué pidió el cliente</p>
                    {selectedLead.brief && (
                      <div className="bg-[#F9F8F6] border-l-2 border-[#D95D39] px-3 py-2.5 mb-3 rounded-sm">
                        <p className="text-sm text-[#1A1F2B] leading-relaxed whitespace-pre-wrap">{selectedLead.brief}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      {selectedLead.destination_interest && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#8A8F98] shrink-0" />
                          <span className="text-[#1A1F2B]"><span className="text-[10px] uppercase tracking-wider text-[#8A8F98] mr-1.5">Destino</span>{selectedLead.destination_interest}</span>
                        </div>
                      )}
                      {selectedLead.travel_dates && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-[#8A8F98] shrink-0" />
                          <span className="text-[#1A1F2B]"><span className="text-[10px] uppercase tracking-wider text-[#8A8F98] mr-1.5">Fechas</span>{selectedLead.travel_dates}</span>
                        </div>
                      )}
                      {selectedLead.travelers && (
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-[#8A8F98] shrink-0" />
                          <span className="text-[#1A1F2B]"><span className="text-[10px] uppercase tracking-wider text-[#8A8F98] mr-1.5">Viajeros</span>{selectedLead.travelers}</span>
                        </div>
                      )}
                      {selectedLead.budget && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-[#8A8F98] shrink-0" />
                          <span className="text-[#1A1F2B]"><span className="text-[10px] uppercase tracking-wider text-[#8A8F98] mr-1.5">Presupuesto</span>{selectedLead.budget}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 3. AI SUMMARY (if any) */}
                {selectedLead.summary && (
                  <div className="py-3 border-b border-[#E2DCD0]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-1.5">Resumen IA del lead</p>
                    <p className="text-sm text-[#1A1F2B] leading-relaxed">{selectedLead.summary}</p>
                  </div>
                )}

                {/* 4. INTERNAL NOTES */}
                {selectedLead.notes && (
                  <div className="py-3 border-b border-[#E2DCD0]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-1.5">Notas internas</p>
                    <pre className="text-xs text-[#1A1F2B] whitespace-pre-wrap font-sans">{selectedLead.notes}</pre>
                  </div>
                )}

                {/* 4.5 MAILCHIMP DATA (read-only view) */}
                {selectedLead.mailchimp_data && Object.keys(selectedLead.mailchimp_data || {}).length > 0 && (
                  <div className="py-3 border-b border-[#E2DCD0]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-2 inline-flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFE01B]" />
                      Datos Mailchimp
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                      {Object.entries(selectedLead.mailchimp_data).map(([k, v]) => {
                        if (!v || (typeof v === "object" && Object.keys(v).length === 0)) return null;
                        const label = mcSchema.find((f) => f.tag === k)?.name || k;
                        const display = typeof v === "object" ? JSON.stringify(v) : String(v);
                        return (
                          <div key={k} className="flex gap-2">
                            <span className="text-[#8A8F98] uppercase tracking-wider text-[9px] min-w-[100px]">{label}</span>
                            <span className="text-[#1A1F2B]">{display}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 5. CHAT CONVERSATION (last) */}
                {selectedLead.conversation && selectedLead.conversation.length > 0 && (
                  <div className="py-3">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-3">Registro de conversación ({selectedLead.conversation.length} mensajes)</p>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto bg-[#F9F8F6] p-3 rounded-sm">
                      {selectedLead.conversation.map((m, i) => (
                        <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                          <div className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 text-[10px] font-medium ${m.role === "user" ? "bg-[#D95D39] text-white" : "bg-[#0A1128] text-white"}`}>
                            {m.role === "user" ? "U" : <Bot className="w-3 h-3" />}
                          </div>
                          <div className={`max-w-[80%] px-3 py-2 rounded-sm text-xs whitespace-pre-wrap ${m.role === "user" ? "bg-[#0A1128] text-white" : "bg-white border border-[#E2DCD0] text-[#1A1F2B]"}`}>
                            {m.content}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </>
                )}

                <DialogFooter className="pt-4 border-t border-[#E2DCD0]">
                  <Link to={`/app/chat?lead=${selectedLead.id}`}>
                    <Button variant="outline" className="rounded-sm border-[#E2DCD0]">
                      <MessageSquare className="w-3 h-3 mr-1" />Abrir chat interno con Maya
                    </Button>
                  </Link>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
